package it.maicol07.gamerlogue.`data`

import it.maicol07.spraypaintkt.CollectionProxy
import it.maicol07.spraypaintkt.RecordProxy
import it.maicol07.spraypaintkt.Resource
import it.maicol07.spraypaintkt.ResourceRegistry
import it.maicol07.spraypaintkt.ResourceSerializer
import it.maicol07.spraypaintkt.Scope
import it.maicol07.spraypaintkt.SortDirection
import it.maicol07.spraypaintkt.extensions.DirtyMap
import it.maicol07.spraypaintkt.extensions.trackChanges
import it.maicol07.spraypaintkt.interfaces.JsonApiConfig
import it.maicol07.spraypaintkt.registerResources
import kotlin.Any
import kotlin.Boolean
import kotlin.Number
import kotlin.String
import kotlin.Unit
import kotlin.collections.MutableMap
import kotlin.reflect.KClass
import kotlinx.serialization.Serializable

@Serializable(Follow.Companion.Serializer::class)
public class Follow : Resource, FollowSchema {
  override val `companion`: Resource.CompanionObj<Follow> = Companion

  override var id: String? = null

  override var isPersisted: Boolean = false

  override val attributes: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val relationships: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val meta: MutableMap<String, Any?> = mutableMapOf()

  override val links: MutableMap<String, Any?> = mutableMapOf()

  override val type: String by lazy { companion.resourceType }

  override var followableId: String
    get() = if (attributes.containsKey("followable_id")) {
      attributes["followable_id"] as String
    } else {
      throw NoSuchElementException("followableId not found")
    }
    set(`value`) {
      attributes["followable_id"] = value
    }

  override var userId: String?
    get() = if (attributes.containsKey("user_id")) {
      attributes["user_id"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["user_id"] = value
    }

  override var createdAt: String?
    get() = if (attributes.containsKey("created_at")) {
      attributes["created_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["created_at"] = value
    }

  public constructor(
    followableId: String? = null,
    userId: String? = null,
    createdAt: String? = null,
  ) {
    if (followableId != null) {
      attributes["followable_id"] = followableId
    }
    if (userId != null) {
      attributes["user_id"] = userId
    }
    if (createdAt != null) {
      attributes["created_at"] = createdAt
    }
  }

  public companion object : Resource.CompanionObj<Follow> {
    override val resourceType: String = "Follow"

    override val endpoint: String = "follows"

    override val config: JsonApiConfig = AppJsonApiConfig

    override val factory: () -> Follow = { Follow() }

    override val schema: KClass<FollowSchema> = FollowSchema::class

    init {
      ResourceRegistry.registerResources()
    }

    public fun scope(options: Scope<Follow>.() -> Unit = {}): Scope<Follow> = Scope(Follow::class, options)

    public final suspend fun all(): CollectionProxy<Follow> = scope().all()

    public final suspend fun find(id: String): RecordProxy<Follow> = scope().find(id)

    public final suspend fun findOrNull(id: String): RecordProxy<Follow?> = scope().findOrNull(id)

    public final suspend fun first(): RecordProxy<Follow> = scope().first()

    public final suspend fun firstOrNull(): RecordProxy<Follow?> = scope().firstOrNull()

    public final suspend fun exists(): Boolean = scope().exists()

    public final suspend fun last(): RecordProxy<Follow> = scope().last()

    public final suspend fun lastOrNull(): RecordProxy<Follow?> = scope().lastOrNull()

    public final fun page(pageNumber: Number): Scope<Follow> = scope().page(pageNumber)

    public final fun per(pageSize: Number): Scope<Follow> = scope().per(pageSize)

    public final fun limit(limit: Number): Scope<Follow> = scope().limit(limit)

    public final fun offset(offset: Number): Scope<Follow> = scope().offset(offset)

    public final fun `where`(attribute: String, `value`: Any): Scope<Follow> = scope().where(attribute, value)

    public final fun extraParam(key: String, `value`: String): Scope<Follow> = scope().extraParam(key, value)

    public final fun order(attribute: String): Scope<Follow> = scope().order(attribute)

    public final fun order(attribute: String, sortDirection: SortDirection): Scope<Follow> = scope().order(attribute, sortDirection)

    public final fun includes(vararg relationships: String): Scope<Follow> = scope().includes(*relationships)

    public final fun select(type: String, vararg fields: String): Scope<Follow> = scope().select(type, *fields)

    public object Serializer : ResourceSerializer<Follow>()
  }
}
